import type {
  PollerEmailDecisionType,
  PollerNonActionableReason,
  PollerSelfSuppressReason,
} from '@app/shared';
import type { StatusBadgeProps } from '../ui';

/**
 * Pure presentation maps for the per-user poller RUN DETAILS surfaces (#318) — the
 * run-detail drill-down and the Poller Activity lookup. Kept framework-free so they run in
 * the cheap `node` test project and stay the SINGLE source of truth for how a decision /
 * reason reads, shared by both surfaces.
 */

/** How one dispatch decision reads: a semantic badge tone + a short human label. */
export const DECISION_META: Record<
  PollerEmailDecisionType,
  { tone: StatusBadgeProps['tone']; label: string }
> = {
  spawned_run: { tone: 'success', label: 'Spawned run' },
  resumed_run: { tone: 'info', label: 'Resumed run' },
  suppressed_self: { tone: 'neutral', label: 'Suppressed (self)' },
  non_actionable: { tone: 'warning', label: 'Non-actionable' },
  no_matching_trigger: { tone: 'neutral', label: 'No matching agent' },
  duplicate: { tone: 'neutral', label: 'Duplicate' },
};

/** Human label for the §17.1 classifier reason carried when `decision = non_actionable`. */
export const REASON_LABELS: Record<PollerNonActionableReason, string> = {
  'noreply-sender': 'No-reply sender',
  'calendar-auto': 'Calendar auto',
  'list-mail': 'List mail',
  'auto-submitted': 'Auto-submitted',
  'bulk-precedence': 'Bulk',
  'delivery-report': 'Delivery report',
};

/** Human label for the #328 self/actionable sub-reason carried when `decision = suppressed_self`. */
export const SELF_SUPPRESS_REASON_LABELS: Record<PollerSelfSuppressReason, string> = {
  'self-sent': 'Sent by account owner',
  'not-in-inbox': 'Not in inbox (archived)',
};

/** Every known reason label (non-actionable + self-suppress), keyed by its raw enum value. */
export const ALL_REASON_LABELS: Record<string, string> = {
  ...REASON_LABELS,
  ...SELF_SUPPRESS_REASON_LABELS,
};

/** The badge tone + label for a decision, with a safe fallback for an unknown value. */
export function decisionMeta(decision: string): { tone: StatusBadgeProps['tone']; label: string } {
  return DECISION_META[decision as PollerEmailDecisionType] ?? { tone: 'neutral', label: decision };
}

/** The human reason label, or the raw reason when it isn't a known enum member. */
export function reasonLabel(reason: string | null): string | null {
  if (!reason) return null;
  return ALL_REASON_LABELS[reason] ?? reason;
}

/** Compact "N{s,m,h,d} ago" from an ISO instant (no live ticker; recomputed each render). */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const diffMs = now - new Date(iso).getTime();
  const s = Math.max(0, Math.round(diffMs / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

/** Full local timestamp for an ISO instant (the `title`/tooltip form), empty for bad input. */
export function absoluteTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/** Short, stable head of an opaque id (e.g. a threadId / connectionId) for a dense cell. */
export function shortId(id: string, head = 8): string {
  return id.length <= head ? id : `${id.slice(0, head)}…`;
}
